/**
 * Gereedschap — DTO's + gedeelde request-schemas voor de Gereedschappen-tab
 * op /materieel (spec docs/specs/19 §9; CRUD = batch-7 fx.5).
 *
 * Gereedschap leeft op de voorraad-uniek-engine: een exemplaar is een
 * `VoorraadUniekItem` van een traceerbaar `VoorraadArtikel` met categorie
 * GEREEDSCHAP (spec 30 Deel 11; het oude Asset-model is uitgefaseerd). De
 * `/gereedschap`-routes zijn dunne, gereedschap-gescopete ingangen op die
 * engine — géén tweede waarheid of eigen uitgiftesysteem.
 */

import { z } from 'zod'

export const GEREEDSCHAP_STATUS = [
  'BESCHIKBAAR',
  'IN_GEBRUIK',
  'IN_ONDERHOUD',
  'DEFECT',
  'AFGESCHREVEN',
] as const
export type GereedschapStatus = (typeof GEREEDSCHAP_STATUS)[number]

export const GEREEDSCHAP_STATUS_LABELS: Record<GereedschapStatus, string> = {
  BESCHIKBAAR: 'Beschikbaar',
  IN_GEBRUIK: 'In gebruik',
  IN_ONDERHOUD: 'In onderhoud',
  DEFECT: 'Defect',
  AFGESCHREVEN: 'Afgeschreven',
}

export interface GereedschapListRowDTO {
  id: string
  assetNumber: string
  name: string
  categorie: string | null
  merk: string | null
  model: string | null
  status: GereedschapStatus
  locatie: string | null
  /** Naam van de medewerker met een actieve toewijzing, indien aanwezig. */
  toegewezenAan: string | null
}

export interface GereedschapStatsDTO {
  totaal: number
  beschikbaar: number
  inGebruik: number
  inOnderhoud: number
}

export interface GereedschapListResponseDTO {
  data: GereedschapListRowDTO[]
  meta: { total: number; page: number; pageSize: number }
}

// ─── Toegewezen-overzicht (Gereedschap → Toegewezen) ────────────────────────
//
// Al het uitgegeven gereedschap, gegroepeerd per medewerker: wie heeft wat.

export interface GereedschapToegewezenItemDTO {
  id: string
  /** Artikelnummer + eventueel serienummer/assetTag-suffix (zelfde vorm als de lijst). */
  assetNumber: string
  naam: string
  merk: string | null
  model: string | null
}

export interface GereedschapToegewezenGroepDTO {
  employeeId: string
  employeeNaam: string
  aantal: number
  items: GereedschapToegewezenItemDTO[]
}

// ─── Gedeelde request-schemas (batch-7 fx.5) ────────────────────────────────
//
// ID-referenties: `z.string().min(1)` (prod heeft legacy UUID-format ids, dus
// NIET `.cuid()`). Optioneel-nullable velden krijgen `.optional().nullable()`
// (null-rejection-guardrail). Datums als yyyy-mm-dd/ISO-string; de route
// parset naar Date. Geld (aanschafBedrag) wordt server-side als Prisma
// Decimal opgeslagen.

/** Datum-string in ISO of `yyyy-mm-dd`. */
const gereedschapDateString = z.string().min(1).max(40)

/**
 * Nieuw gereedschap-exemplaar. Het soort gereedschap is het artikel: kies een
 * bestaand traceerbaar GEREEDSCHAP-artikel (`artikelId`) óf geef een `naam` —
 * de API hergebruikt dan het bestaande artikel met die naam of maakt er één
 * aan (find-or-create; geen dubbele soorten).
 */
export const createGereedschapSchema = z
  .object({
    artikelId: z.string().min(1).optional().nullable(),
    naam: z.string().trim().min(1).max(200).optional().nullable(),
    merk: z.string().max(100).optional().nullable(),
    model: z.string().max(100).optional().nullable(),
    serienummer: z.string().max(200).optional().nullable(),
    assetTag: z.string().max(200).optional().nullable(),
    aankoopdatum: gereedschapDateString.optional().nullable(),
    aanschafBedrag: z.number().nonnegative().optional().nullable(),
    locatieId: z.string().min(1).optional().nullable(),
    keuringsdatum: gereedschapDateString.optional().nullable(),
    volgendeKeuring: gereedschapDateString.optional().nullable(),
    opmerkingen: z.string().max(2000).optional().nullable(),
    /**
     * Direct toewijzen aan een medewerker: loopt via de bestaande
     * uitgifte-flow van de uniek-engine (UitrustingItem-koppeling) — geen
     * tweede uitgiftesysteem.
     */
    employeeId: z.string().min(1).optional().nullable(),
  })
  .refine((v) => Boolean(v.artikelId) || Boolean(v.naam && v.naam.trim().length > 0), {
    message: 'Kies een artikel of vul een naam in',
    path: ['naam'],
  })
export type CreateGereedschapInput = z.infer<typeof createGereedschapSchema>

/** Bewerken van een exemplaar (artikel/naam wijzigt niet mee — dat is de soort). */
export const updateGereedschapSchema = z.object({
  merk: z.string().max(100).optional().nullable(),
  model: z.string().max(100).optional().nullable(),
  serienummer: z.string().max(200).optional().nullable(),
  assetTag: z.string().max(200).optional().nullable(),
  aankoopdatum: gereedschapDateString.optional().nullable(),
  aanschafBedrag: z.number().nonnegative().optional().nullable(),
  locatieId: z.string().min(1).optional().nullable(),
  keuringsdatum: gereedschapDateString.optional().nullable(),
  volgendeKeuring: gereedschapDateString.optional().nullable(),
  opmerkingen: z.string().max(2000).optional().nullable(),
})
export type UpdateGereedschapInput = z.infer<typeof updateGereedschapSchema>

/**
 * Statuswijziging in GereedschapStatus-termen; de API mapt op de
 * uniek-engine-acties: IN_GEBRUIK → uitgeven (employeeId verplicht),
 * BESCHIKBAAR → innemen (locatieId verplicht), IN_ONDERHOUD/DEFECT →
 * statuswijziging, AFGESCHREVEN → afschrijven (soft-delete, zelfde pad als
 * DELETE).
 */
export const wijzigGereedschapStatusSchema = z
  .object({
    status: z.enum(GEREEDSCHAP_STATUS),
    employeeId: z.string().min(1).optional().nullable(),
    locatieId: z.string().min(1).optional().nullable(),
    reden: z.string().max(500).optional().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.status === 'IN_GEBRUIK' && !v.employeeId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['employeeId'],
        message: 'Kies een medewerker om het gereedschap aan uit te geven',
      })
    }
    if (v.status === 'BESCHIKBAAR' && !v.locatieId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['locatieId'],
        message: 'Kies een locatie om het gereedschap op in te nemen',
      })
    }
  })
export type WijzigGereedschapStatusInput = z.infer<typeof wijzigGereedschapStatusSchema>
